/**
 * The Business Opportunities module: the viewer's own ideas, then the top five
 * beside the status, verdict and industry cards. It owns the reads it needs,
 * so a failure shows its Retry inside the module and leaves the page alone.
 */

import { useState } from 'react';

import { useDashboard, useIdeaCatalogue, useIdeaPoints } from '@/api/queries';
import type { IdeaFilterParams } from '@/api/types';
import { MatrixTiles } from '@/routes/business-opportunities/MatrixTiles';
import { ScoringGuideDialog } from '@/routes/business-opportunities/ScoringGuideDialog';
import { opportunitiesHref } from '@/routes/business-opportunities/ideaFilters';
import { LoadFailure, LoadingCard } from '@/ui/StateCard';

import { DashboardModule } from './DashboardModule';
import { IndustryCard } from './IndustryCard';
import { MineCard } from './MineCard';
import { TopIdeasCard } from './TopIdeasCard';

// Active ideas, best first: the status card still counts every status (the
// points count each card under every filter but its own).
const ACTIVE: IdeaFilterParams = { dispositions: ['active'], industries: [], outcomes: [] };

function MineArea({ dashboard }: { dashboard: ReturnType<typeof useDashboard> }) {
  if (dashboard.error) return <LoadFailure error={dashboard.error} onRetry={() => void dashboard.refetch()} />;
  if (!dashboard.data) return <LoadingCard label="Loading your ideas" />;
  return (
    <div className="metric-area">
      <MineCard data={dashboard.data.mine} />
    </div>
  );
}

export function BusinessOpportunitiesModule() {
  // The page reads the same query for Needs attention; the cache shares it, and this component re-renders on its own result.
  const dashboard = useDashboard();
  const catalogue = useIdeaCatalogue();
  const ideas = useIdeaPoints(ACTIVE);
  const [showGuide, setShowGuide] = useState(false);

  const module = (children: React.ReactNode) => (
    <DashboardModule id="business-opportunities" title="Business Opportunities">
      {children}
    </DashboardModule>
  );

  if (catalogue.isPending || ideas.isPending) return module(<LoadingCard label="Loading the dashboard" />);
  if (catalogue.error) return module(<LoadFailure error={catalogue.error} onRetry={() => void catalogue.refetch()} />);
  if (ideas.error) return module(<LoadFailure error={ideas.error} onRetry={() => void ideas.refetch()} />);

  const data = dashboard.data;
  return (
    <>
      {module(
        <>
          <MineArea dashboard={dashboard} />

          <div className="dash-grid">
            <TopIdeasCard points={ideas.data.points} />

            <MatrixTiles
              counts={ideas.data.counts}
              outcomeCounts={ideas.data.verdict_counts}
              scoredNoun="active scored"
              dispositionHref={(d) => opportunitiesHref({ view: 'list', dispositions: [d] })}
              outcomeHref={(o) => opportunitiesHref({ view: 'list', dispositions: ['active'], outcomes: [o] })}
              onOpenGuide={() => setShowGuide(true)}
              stacked
            >
              {data && <IndustryCard data={data.industries} />}
            </MatrixTiles>
          </div>
        </>,
      )}
      {showGuide && <ScoringGuideDialog dimensions={catalogue.data.dimensions} threshold={catalogue.data.matrix_threshold} onClose={() => setShowGuide(false)} />}
    </>
  );
}
